import { useDispatch, useSelector } from 'react-redux';
import { useTranslation, Trans } from 'react-i18next';
import {
    Card,
    CardHeader,
    CardContent,
    Avatar,
    Typography,
    Select,
    MenuItem,
    Switch,
    FormControlLabel,
} from '@material-ui/core';
import { AdditionalInfo } from '@commonClient2/components';
import {
    UPDATE_SETUP,
} from '../../../store/setup';
import {
    QualificationRule, BidProcessType, UserLicense, UserLicenseNames,
} from '../../../../Enums';
import { getQualificationRuleLabel } from '../../../../Enums/Helpers/LabellingHelper';
import { hasLicense } from '../../../../services/JwtService';

const InputTitle = (props) => (
    <Typography
        component="p"
        className="input-label"
        {...props}
    >
        {props.children}
    </Typography>
);

const InputDisclaimer = (props) => (
    <p {...props}>
        <small className="input-disclaimer">
            {props.children}
        </small>
    </p>
);

const StyledSelect = (props) => (
    <Select
        MenuProps={{
            anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left',
            },
            transformOrigin: {
                vertical: 'top',
                horizontal: 'left',
            },
            getContentAnchorEl: null,
        }}
        variant="outlined"
        className="sue-select-full-width"
        defaultValue=""
        {...props}
    >
        {props.children}
    </Select>
);

/**
 * @param {{
 *  stepIndex: number
 *  validationErrors: {[Key: string] : boolean}
 *  disabledSections: {[Key: string] : boolean}
 *  errorRef: import('react').MutableRefObject<HTMLInputElement>
 * }} props
 */
function RuleSection({
    stepIndex,
    validationErrors,
    disabledSections,
    errorRef,
}) {
    const dispatch = useDispatch();
    const { t } = useTranslation('shiftBidding', { keyPrefix: 'admin.setupEdit.rulesStep' });
    const { t: tCommon } = useTranslation('shiftBidding');
    /** @type {ShiftBidding.AdminStore} */
    // @ts-ignore
    const { adminSetup, adminAppData, publicConfigs } = useSelector((state) => state);
    const {
        qualificationRule,
        awardingRuleSetId,
        isEmployeeMustSubmitRequiredAmountOfBids,
        isEmployeeCanDeclineBid,
        isBidOnBehalfAllowedWhenActive,
        isBidOnBehalfAllowedWhenClosed,
        isEditBidOnBehalfAllowedWhenActive,
        isEditBidOnBehalfAllowedWhenClosed,
        isRecalculateEmployeeRanksAllowedWhenActiveOrClosed,
        isEnableEmployeeRankReport,
        isEnableEmployeeBidResultReport,
        bidProcessType,
    } = adminSetup;
    const { awardingRules } = adminAppData;
    const { isUseUkgReadyTerminology } = publicConfigs;

    /**
     * @typedef {{value: QualificationRule, label: string}} SelectOption
     * @type {SelectOption[]}
     */
    const qualificationRuleOptions = Object.values(QualificationRule).map((x) => ({ value: x, label: getQualificationRuleLabel(x, isUseUkgReadyTerminology) }));
    const getQualificationRuleValue = () => (qualificationRule === -1
        ? '' : qualificationRuleOptions.find((x) => x.value === qualificationRule).value);
    /**
    * @type {SelectOption[]}
    */
    const awardingRuleOptions = awardingRules.map((x) => ({ value: x.ruleSetId, label: x.name }));
    const getAwardingRuleValue = () => (awardingRuleSetId === -1
        ? '' : awardingRuleOptions.find((x) => x.value === awardingRuleSetId).value);

    /**
     * @param {string} target
     * @param {QualificationRule | number | boolean} value
     */
    const updateSetupStore = (target, value) => {
        dispatch({
            type: UPDATE_SETUP,
            payload: {
                [target]: value,
            },
        });
    };

    /** @param {number} ruleOption */
    const isQualificationDisabled = (ruleOption) => (
        ruleOption === QualificationRule.MatchQualification && !hasLicense(UserLicenseNames[UserLicense.AdvancedScheduling])
    );

    return (
        <Card className="card">
            <CardHeader
                ref={errorRef}
                className="card--header"
                color="textSecondary"
                gutterBottom
                avatar={<Avatar className="avatar">{stepIndex}</Avatar>}
                title={t('title')}
            />
            <CardContent>
                <div>
                    <InputTitle>{t('qualificationRulesInput.label')}</InputTitle>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <StyledSelect
                            labelId="select-qualification-rules-label"
                            id="select-qualification-rules"
                            className="sue-select-with-additional-info"
                            error={validationErrors.qualificationRule}
                            disabled={disabledSections.qualificationRule}
                            renderValue={() => qualificationRuleOptions.find((x) => x.value === qualificationRule)?.label}
                            value={getQualificationRuleValue()}
                            onChange={(/** @type {React.ChangeEvent<{ name?: string; value: number }>} */
                                event,
                            ) => updateSetupStore('qualificationRule', event.target.value)}
                        >
                            {qualificationRuleOptions.map((x) => (
                                <MenuItem
                                    value={x.value}
                                    key={`SelectItem_${x.label}`}
                                    disabled={isQualificationDisabled(x.value)}
                                >
                                    {x.label}
                                </MenuItem>
                            ))}
                        </StyledSelect>
                        <AdditionalInfo className="additionalInfo">
                             <Trans
                                i18nKey="shiftBidding:admin.setupEdit.rulesStep.qualificationRulesInput.helpText"
                                values={{
                                    primaryJob: tCommon(isUseUkgReadyTerminology ? 'common.terminology.primaryJob_ukgReady' : 'common.terminology.primaryJob'),
                                    jobTransferSet: tCommon(isUseUkgReadyTerminology ? 'common.terminology.jobTransferSet_ukgReady' : 'common.terminology.jobTransferSet'),
                                    matchPrimaryJobAndJobTransferSet: getQualificationRuleLabel(QualificationRule.MatchPrimaryJobAndJobTransferSet, isUseUkgReadyTerminology),
                                    matchPrimaryJobOnly: getQualificationRuleLabel(QualificationRule.MatchPrimaryJobOnly, isUseUkgReadyTerminology),
                                }}
                                components={{
                                    0: <li />,
                                    1: <strong />,
                                    2: <br />,
                                    3: <ul />,
                                }}
                            />
                        </AdditionalInfo>
                    </div>
                    {
                        validationErrors.qualificationRule
                            ? <span style={{ color: 'red' }}>{t('qualificationRulesInput.error')}</span> : null
                    }
                </div>
                <div>
                    <InputTitle>{t('awardingRulesInput.label')}</InputTitle>
                    <StyledSelect
                        labelId="select-awarding-rules-label"
                        id="select-awarding-rules"
                        error={validationErrors.awardingRuleSetId}
                        disabled={disabledSections.awardingRuleSetId}
                        renderValue={() => awardingRuleOptions.find((x) => x.value === awardingRuleSetId)?.label}
                        value={getAwardingRuleValue()}
                        onChange={(/** @type {React.ChangeEvent<{ name?: string; value: number }>} */
                            event,
                        ) => updateSetupStore('awardingRuleSetId', event.target.value)}
                    >
                        {awardingRuleOptions.map((x) => (
                            <MenuItem
                                value={x.value}
                                key={`SelectItem_${x.label}`}
                            >
                                {x.label}
                            </MenuItem>
                        ))}
                    </StyledSelect>
                    {
                        validationErrors.awardingRuleSetId ? <span style={{ color: 'red' }}>{t('awardingRulesInput.error')}</span> : null
                    }
                </div>
                <InputTitle>{t('managerRules')}</InputTitle>
                <div>
                    <FormControlLabel
                        control={(
                            <Switch
                                checked={isBidOnBehalfAllowedWhenActive}
                                disabled={disabledSections.isBidOnBehalfAllowedWhenActive}
                                onChange={(event) => updateSetupStore('isBidOnBehalfAllowedWhenActive', event.target.checked)}
                                color="primary"
                            />
                        )}
                        label={t('isBidOnBehalfAllowedWhenActiveInput.label')}
                        className="sue-form-control"
                    />
                </div>
                <div>
                    <FormControlLabel
                        control={(
                            <Switch
                                checked={isBidOnBehalfAllowedWhenClosed}
                                disabled={disabledSections.isBidOnBehalfAllowedWhenClosed}
                                onChange={(event) => updateSetupStore('isBidOnBehalfAllowedWhenClosed', event.target.checked)}
                                color="primary"
                            />
                        )}
                        label={t('isBidOnBehalfAllowedWhenClosedInput.label')}
                        className="sue-form-control"
                    />
                    <InputDisclaimer>
                        {t('isBidOnBehalfAllowedWhenClosedInput.disclaimer')}
                    </InputDisclaimer>
                </div>
                <div>
                    <FormControlLabel
                        control={(
                            <Switch
                                checked={isEditBidOnBehalfAllowedWhenActive}
                                disabled={disabledSections.isEditBidOnBehalfAllowedWhenActive}
                                onChange={(event) => updateSetupStore('isEditBidOnBehalfAllowedWhenActive', event.target.checked)}
                                color="primary"
                            />
                        )}
                        label={t('isEditBidOnBehalfAllowedWhenActiveInput.label')}
                        className="sue-form-control"
                    />
                </div>
                <div>
                    <FormControlLabel
                        control={(
                            <Switch
                                checked={isEditBidOnBehalfAllowedWhenClosed}
                                disabled={disabledSections.isEditBidOnBehalfAllowedWhenClosed}
                                onChange={(event) => updateSetupStore('isEditBidOnBehalfAllowedWhenClosed', event.target.checked)}
                                color="primary"
                            />
                        )}
                        label={t('isEditBidOnBehalfAllowedWhenClosedInput.label')}
                        className="sue-form-control"
                    />
                    <InputDisclaimer>
                        {t('isEditBidOnBehalfAllowedWhenClosedInput.disclaimer')}
                    </InputDisclaimer>
                </div>
                {
                    bidProcessType === BidProcessType.Preferential 
                        ? (
                            <div>
                                <FormControlLabel
                                    control={(
                                        <Switch
                                            checked={isRecalculateEmployeeRanksAllowedWhenActiveOrClosed}
                                            disabled={disabledSections.isRecalculateEmployeeRanksAllowedWhenActiveOrClosed}
                                            onChange={(event) => updateSetupStore('isRecalculateEmployeeRanksAllowedWhenActiveOrClosed', event.target.checked)}
                                            color="primary"
                                        />
                                    )}
                                    label={t('isRecalculateEmployeeRanksAllowedWhenActiveOrClosedInput.label')}
                                    className="sue-form-control"
                                />
                                <InputDisclaimer>
                                    {t('isRecalculateEmployeeRanksAllowedWhenActiveOrClosedInput.disclaimer')}
                                </InputDisclaimer>
                            </div>
                        ) 
                        : null
                }
                <InputTitle>{t('employeeRules')}</InputTitle>
                <div>
                    <FormControlLabel
                        control={(
                            <Switch
                                checked={isEmployeeCanDeclineBid}
                                disabled={disabledSections.isEmployeeCanDeclineBid}
                                onChange={(event) => updateSetupStore('isEmployeeCanDeclineBid', event.target.checked)}
                                color="primary"
                            />
                        )}
                        label={t('isEmployeeCanDeclineBidInput.label')}
                        className="sue-form-control"
                    />
                    <InputDisclaimer>
                        {t('isEmployeeCanDeclineBidInput.disclaimer')}
                    </InputDisclaimer>
                </div>
                {bidProcessType === BidProcessType.Preferential ? (
                    <div>
                        <FormControlLabel
                            control={(
                                <Switch
                                    checked={isEmployeeMustSubmitRequiredAmountOfBids}
                                    disabled={disabledSections.employeeMustSubmitRequiredAmountOfBids}
                                    onChange={(event) => updateSetupStore('isEmployeeMustSubmitRequiredAmountOfBids', event.target.checked)}
                                    color="primary"
                                />
                            )}
                            label={t('isEmployeeMustSubmitRequiredAmountOfBidsInput.label')}
                            className="sue-form-control"
                        />
                        <InputDisclaimer>
                            {t('isEmployeeMustSubmitRequiredAmountOfBidsInput.disclaimer')}
                        </InputDisclaimer>
                    </div>
                ) : null}
                <div>
                    <FormControlLabel
                        control={(
                            <Switch
                                checked={isEnableEmployeeRankReport}
                                onChange={(event) => updateSetupStore('isEnableEmployeeRankReport', event.target.checked)}
                                color="primary"
                            />
                        )}
                        label={t('isEnableEmployeeRankReportInput.label')}
                        className="sue-form-control"
                        disabled={disabledSections.isEnableEmployeeRankReport}
                    />
                </div>
                <div>
                    <FormControlLabel
                        control={(
                            <Switch
                                checked={isEnableEmployeeBidResultReport}
                                onChange={(event) => updateSetupStore('isEnableEmployeeBidResultReport', event.target.checked)}
                                color="primary"
                            />
                        )}
                        label={t('isEnableEmployeeBidResultReportInput.label')}
                        className="sue-form-control"
                        disabled={disabledSections.isEnableEmployeeBidResultReport}
                    />
                </div>
            </CardContent>
        </Card>

    );
}

export default RuleSection;
